/**
 * AB#21521 — the reason-code list as served by the API.
 *
 * `GET shipping-date-reason-codes` → `{ result: { reason_codes: [{ code, label, requires_detail }] } }`,
 * straight from `App\Enums\ShippingDateChangeReason`. Fetching it means the vocabulary can be
 * realigned with the sheet shared with the customer by editing the enum alone, instead of
 * editing the enum AND `constants/shippingDateChangeReasons.ts` and shipping two releases in
 * step. That constant stays the fallback for when this endpoint is unreachable.
 */
import type { ShippingDateChangeReason } from '@/utils/constants/shippingDateChangeReasons';

function extractRows(body: unknown): unknown[] {
  if (body === null || body === undefined) return [];
  if (Array.isArray(body)) return body;
  if (typeof body !== 'object') return [];
  const o = body as Record<string, unknown>;
  if (Array.isArray(o.reason_codes)) return o.reason_codes;
  const result = o.result;
  if (Array.isArray(result)) return result;
  if (result && typeof result === 'object') {
    const r = result as Record<string, unknown>;
    if (Array.isArray(r.reason_codes)) return r.reason_codes;
  }
  if (Array.isArray(o.data)) return o.data;
  return [];
}

function toRow(item: unknown): ShippingDateChangeReason | null {
  if (!item || typeof item !== 'object') return null;
  const { code, label, requires_detail } = item as Record<string, unknown>;
  if (typeof code !== 'string' || typeof label !== 'string') return null;
  const c = code.trim();
  const l = label.trim();
  if (!c || !l) return null;

  return { code: c, label: l, requiresDetail: requires_detail === true };
}

export function parseShippingDateReasonCodesResponse(body: unknown): ShippingDateChangeReason[] {
  const out: ShippingDateChangeReason[] = [];
  const seen = new Set<string>();
  for (const item of extractRows(body)) {
    const row = toRow(item);
    if (row && !seen.has(row.code)) {
      seen.add(row.code);
      out.push(row);
    }
  }
  return out;
}
